/**
 * Staff worksheet "First Time Entry" / "Last Time Entry" columns (task 13583).
 *
 * The API returns both as DateOnly (`yyyy-MM-dd`, no time, no zone). They must be
 * parsed as LOCAL calendar dates: `new Date('2026-11-03')` is UTC midnight, which
 * `toLocaleDateString()` renders as 11/02 anywhere west of Greenwich.
 */

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})/;

/** Parse a `yyyy-MM-dd` (or any ISO string starting with one) as a local date; null when absent or unparseable. */
export function parseDateOnly(value: string | null | undefined): Date | null {
    if (!value) return null;
    const m = DATE_ONLY.exec(value);
    if (!m) return null;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return isNaN(d.getTime()) ? null : d;
}

/** Locale-formatted date for a DateOnly string; empty string when absent. */
export function formatDateOnly(value: string | null | undefined): string {
    const d = parseDateOnly(value);
    return d ? d.toLocaleDateString() : '';
}

/**
 * Highlight rule: first and last time entry are the SAME calendar date and that date is in the
 * current calendar month (local). Null on either side = no highlight. Compared by date only.
 */
export function isSingleDayTimeEntryThisMonth(
    first: string | null | undefined,
    last: string | null | undefined,
    now: Date = new Date()
): boolean {
    const f = parseDateOnly(first);
    const l = parseDateOnly(last);
    if (!f || !l) return false;
    if (f.getTime() !== l.getTime()) return false;
    return f.getFullYear() === now.getFullYear() && f.getMonth() === now.getMonth();
}

/** Tailwind classes for a highlighted time-entry cell (the worksheet's standard green). */
export const TIME_ENTRY_HIGHLIGHT_CLASS = 'bg-green-100 text-green-800';
